import { getFundNames } from "../getFundNames/getFundNamesService";
import { getManagers } from "../getManagers/getManagersService";
import { ReportCategory } from "../../enums/Reports";
import {
  StewardshipParams,
  StewardshipCodeParams,
  TcfdRequestParams,
  EngagementParams,
} from "../../models/reportModels";
import { Filter as Option } from "../../types/PageFilterTypes";
import { defaultReportItem } from "../../pages/ReportsList/reportsConfig";
import {
  CommonDataItem,
  CommonDataRequestType,
  CommonDataResponse,
  DistinctFundsRequest,
  ManagerItem,
} from "../../types/CommonDataRequestTypes";
import { FundNamesItem } from "../../types/CommonDataRequestTypes";
import { reportsListDefaultItems } from "../../pages/ReportsList/reportsConfig";
import { ReportItem } from "../../pages/ReportsList/reportsConfig";
import { Status } from "../../enums/Status";
import { CompassError } from "../../types/errorTypes";

export class BuildReportConfig {
  private reportType: ReportCategory;
  private authToken: string;
  private defaultConfig: ReportItem[];
  public error: CompassError | null = null;

  constructor(reportType: ReportCategory, authToken: string) {
    this.reportType = reportType;
    this.authToken = authToken;
    this.defaultConfig = reportsListDefaultItems;
  }

  public async buildParameters(): Promise<ReportItem> {
    // Common logic for building parameters
    let config: ReportItem = defaultReportItem;

    // Specific logic based on report type
    switch (this.reportType) {
      case ReportCategory.TCFD:
        config = await this.buildTCFDReportParameters(
          this.defaultConfig.filter(
            (item) => item.title === ReportCategory.TCFD
          )[0]
        );
        break;
      case ReportCategory.STEWARDSHIP:
        config = await this.buildStewardshipReportParams(
          this.defaultConfig.filter(
            (item) => item.title === ReportCategory.STEWARDSHIP
          )[0]
        );
        break;
      case ReportCategory.STEWARDSHIP_CODE:
        config = await this.buildStewardshipCodeReportParams(
          this.defaultConfig.filter(
            (item) => item.type === ReportCategory.STEWARDSHIP_CODE
          )[0]
        );
        break;
      case ReportCategory.ENGAGEMENT:
        config = await this.buildEngagementReportParams(
          this.defaultConfig.filter(
            (item) => item.type === ReportCategory.ENGAGEMENT
          )[0]
        );
        break;
      case ReportCategory.SDR:
        config = defaultReportItem;
        break;
    }

    return config;
  }

  private async getFundNames(): Promise<FundNamesItem[]> {
    const parameters: CommonDataItem<DistinctFundsRequest> = {
      name: CommonDataRequestType.DISTINCT_FUNDS,
      item: { distinct_funds: null },
    };
    const {
      status,
      data: funds,
      error,
    }: CommonDataResponse<FundNamesItem[]> = await getFundNames(
      parameters,
      this.authToken
    );

    if (status === Status.ERROR) {
      throw error as CompassError;
    }

    return funds as FundNamesItem[];
  }

  private async getManagers(): Promise<ManagerItem[]> {
    const {
      status,
      data: funds,
      error,
    }: CommonDataResponse<ManagerItem[]> = await getManagers(
      null,
      this.authToken
    );

    if (status === Status.ERROR) {
      throw error as CompassError;
    }

    return funds as ManagerItem[];
  }

  private async buildTCFDReportParameters(
    baseItem: ReportItem
  ): Promise<ReportItem> {
    const funds: FundNamesItem[] = await this.getFundNames();
    const indexes: Option[] = funds
      .filter((fund) => fund.is_index)
      .map((fund) => ({
        displayValue: fund.portfolio_index_id,
        key: fund.portfolio_index_id,
        selectorName: "pIndex",
      }));

    const portfolios: Option[] = funds
      .filter((fund) => !fund.is_index)
      .map((fund) => ({
        displayValue: fund.portfolio_index_id,
        key: fund.portfolio_index_id,
        selectorName: "pPortfolio",
      }));

    const requestParams: TcfdRequestParams = {
      pIndex: "",
      pPortfolio: "",
    };

    return {
      ...baseItem,
      requestParams,
      allParamOptions: [indexes, portfolios],
    };
  }

  private async buildStewardshipReportParams(
    baseItem: ReportItem
  ): Promise<ReportItem> {
    const allManagers: ManagerItem[] = await this.getManagers();
    const managerBenchmarks: Option[] = allManagers.map((manager) => ({
      displayValue: manager.manager_name,
      key: manager.manager_id,
      selectorName: "pBenchmark",
    }));

    const managers: Option[] = allManagers.map((manager) => ({
      displayValue: manager.manager_name,
      key: manager.manager_id,
      selectorName: "pManager",
    }));

    const requestParams: StewardshipParams = {
      pManager: "",
      pBenchmark: "",
    };

    return {
      ...baseItem,
      requestParams,
      allParamOptions: [managerBenchmarks, managers],
    };
  }

  private async buildStewardshipCodeReportParams(
    baseItem: ReportItem
  ): Promise<ReportItem> {
    const allManagers: ManagerItem[] = await this.getManagers();
    const benchmarks: Option[] = allManagers.map((manager) => ({
      displayValue: manager.manager_name,
      key: manager.manager_id,
      selectorName: "pBenchmark",
    }));

    const requestParams: StewardshipCodeParams = {
      pBenchmark: "",
    };

    return {
      ...baseItem,
      requestParams,
      allParamOptions: [benchmarks],
    };
  }

  private async buildEngagementReportParams(
    baseItem: ReportItem
  ): Promise<ReportItem> {
    const allManagers: ManagerItem[] = await this.getManagers();
    const managers: Option[] = allManagers.map((manager) => ({
      displayValue: manager.manager_name,
      key: manager.manager_id,
      selectorName: "pManager",
    }));

    const requestParams: EngagementParams = {
      pManager: "",
    };

    return {
      ...baseItem,
      requestParams,
      allParamOptions: [managers],
    };
  }
}
